/**
 * User Fork Detection and Setup Generation
 *
 * Determines whether a trace represents a new user or existing user flow,
 * and generates the appropriate STU (test utility) setup code.
 */

import type {
  CapturedConsumerInfo,
  CapturedIntakeResponses,
  UserFork,
} from "./types";
import { escapeString } from "./utils";

// ============================================================================
// User Fork Detection
// ============================================================================

/**
 * Detect user fork based on state path
 * If otpInput state appears, user went through OTP = new user
 * If otpInput is absent, user was recognized = existing user
 */
export function detectUserFork(statePath: string[]): UserFork {
  return statePath.includes("otpInput") ? "existing_user" : "new_user";
}

// ============================================================================
// STU Setup Generation
// ============================================================================

/**
 * Generate STU setup code for existing user fork
 *
 * IMPORTANT: The fixture ID is embedded in the consumer's lastName (not memberId).
 * This is because getInsuranceStatus overwrites the insurance with fixture data,
 * which would replace the memberId. The lastName persists and is used by the
 * pricing gateway to detect fixture mode.
 */
export function generateExistingUserSetup(
  consumerInfo: CapturedConsumerInfo,
  intakeResponses: CapturedIntakeResponses,
): string {
  // Note: sex is no longer hardcoded - we use consumer.sex dynamically in the generated code
  const payer = consumerInfo.insurancePayer || "Aetna";
  const state = consumerInfo.insuranceState || "NY";

  // Generate intake responses array for createSavedIntakeResponses
  // Note: response must always be a string (GraphQL schema constraint)
  // Objects are JSON stringified before storage
  const intakeResponseEntries = Object.entries(intakeResponses);
  const intakeResponsesSection =
    intakeResponseEntries.length > 0
      ? `
    .createSavedIntakeResponses([
${intakeResponseEntries
  .filter(([_, response]) => response != null)
  .map(([questionId, response]) => {
    // Always stringify - simple strings stay as-is, objects become JSON strings
    const responseValue =
      typeof response === "string"
        ? `"${escapeString(response)}"`
        : `JSON.stringify(${JSON.stringify(response)})`;
    return `      { intakeQuestionId: ${questionId}, response: ${
      responseValue ?? null
    } },`;
  })
  .join("\n")}
    ])`
      : "";

  return `
  // Existing user fork - pre-create user/consumer/insurance in database
  // NOTE: Fixture ID is embedded in lastName (not memberId) because getInsuranceStatus
  // overwrites insurance data including memberId. lastName is used by pricing gateway
  // to detect S3 fixture mode.
  const fixtureLastName = \`TESTSTHREE\${FIXTURE_ID}\`;
  const createdData = await dataSetup
    .createUser(
      UserBuilder.create()
        .withName(consumer.firstName, fixtureLastName)
        .withPhone(consumer.phone)
        .withEmail(consumer.email)
        .withUserAccountStatus(UserAccountStatus.Connected)
        .withOrganization("MEDRITE")
        .build(),
    )
    .createConsumer(
      ConsumerBuilder.create()
        .withName(consumer.firstName, fixtureLastName)
        .withDateOfBirth(format(consumer.dateOfBirth, "yyyy-MM-dd"))
        .withConsumerAccountStatus(ConsumerAccountStatus.Connected)
        .asEhrPatient()
        .withSex(consumer.sex === "Male" ? "MALE" : "FEMALE")
        .withAddress({
          streetAddress: consumer.address?.streetAddress ?? "199 Water St",
          city: consumer.address?.city ?? "New York",
          state: consumer.address?.stateAbbreviated ?? "NY",
          zipCode: consumer.address?.zipCode ?? "10038",
          country: consumer.address?.country ?? "USA",
        })
        .build(),
    )
    .createInsurance(
      InsuranceBuilder.create()
        .withPayer("${payer}")
        .withMemberId("TEST-MEMBER-ID")
        .withState("${state}")
        .withIsPrimaryInsurance(true)
        .withStatus(InsurancesStatusEnum.Active)
        .withInsuranceAccountStatus(InsuranceAccountStatus.Connected)
        .withLastCheckedAt(new Date())
        .withPriority(1)
        .withInsuredFirstName(consumer.firstName)
        .withInsuredLastName(fixtureLastName)
        .build(),
    )${intakeResponsesSection}
    .execute();

  // Update consumer with database IDs
  consumer.consumerId = createdData.consumers[0].id;
  consumer.ehrId = createdData.consumers[0].ehrPatientId ?? "test-patient-id";
  // Update consumer lastName to match the fixture name for UI interactions
  consumer.lastName = fixtureLastName;
`;
}

// ============================================================================
// S3 Fixture Helpers
// ============================================================================

/**
 * Get the S3 fixture lastName for use in tests
 * This is the value that should be used as consumer lastName to trigger mock mode
 *
 * NOTE: We use lastName (not memberId) because getInsuranceStatus overwrites
 * the insurance data including memberId. lastName persists through the flow.
 * Format: TESTSTHREE{fixtureId} (letters only - EHR compatible)
 */
export function getS3FixtureLastName(fixtureId: string): string {
  return `TESTSTHREE${fixtureId}`;
}

/**
 * @deprecated Use getS3FixtureLastName instead. MemberId gets overwritten by getInsuranceStatus.
 */
export function getS3FixtureMemberId(fixtureId: string): string {
  return getS3FixtureLastName(fixtureId);
}
